import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@redux/hooks';

import {
  candidateSubtitle,
  isStatusLoading,
  normalizeCandidateStatus,
} from '@utils';

import {
  candidateDetailsSelectors,
  provideCandidateFeedback,
  provideClientInterviewFeedback,
  setCandidateDetailsAction,
} from '@redux/candidateDetails';

import { CandidateFeedbackFormValues } from '@types';
import {
  CandidateDetailsAction,
  CandidateStatus,
  ClientFeedbackResolution,
  SearchParams,
} from '@constants';
import { logEvent } from '@services';
import { ClientFeedbackDialog } from './ClientFeedbackDialog';

export const ClientFeedbackModal = () => {
  const dispatch = useAppDispatch();
  const [, setSearchParams] = useSearchParams();

  const {
    provideClientFeedbackAPIStatus,
    provideClientInterviewFeedbackAPIStatus,
    writeError,
    data,
  } = useAppSelector(candidateDetailsSelectors.getCandidateDetailsData);
  const candidateDetailsAction = useAppSelector(
    candidateDetailsSelectors.getCandidateDetailsAction,
  );

  const candidateStatus = normalizeCandidateStatus(data?.status);
  const latestCI = data?.interview?.at(-1) || null;
  const isNextRound =
    candidateDetailsAction === CandidateDetailsAction.SCHEDULE_NEXT_ROUND;

  const isLoading =
    isStatusLoading(provideClientFeedbackAPIStatus) ||
    isStatusLoading(provideClientInterviewFeedbackAPIStatus);

  const handleModalClose = () => {
    dispatch(setCandidateDetailsAction(null));
    setSearchParams(
      (_searchParams) => {
        _searchParams.delete(SearchParams.ACTION);
        return _searchParams;
      },
      { replace: true },
    );
  };

  const handleSubmit = async (values: CandidateFeedbackFormValues) => {
    if (!data?.id) return false;

    const res = [
      CandidateDetailsAction.APPROVE_CANDIDATE,
      CandidateDetailsAction.SCHEDULE_INTERVIEW,
      CandidateDetailsAction.SKIP_CANDIDATE,
    ].includes(candidateDetailsAction!)
      ? await dispatch(
          provideCandidateFeedback({
            candidateId: data.id,
            resolution: values.resolution!,
            rejectionReasons: values.rejectionReasons,
            interviewSlots: values.interviewSlots,
            clientCalendar: values.clientCalendar,
            details: values.details,
            clientTimezone: values.clientTimezone,
          }),
        )
      : await dispatch(
          provideClientInterviewFeedback({
            candidateId: data.id,
            resolution: values.resolution!,
            rejectionReasons: values.rejectionReasons,
            interviewSlots: values.interviewSlots,
            clientCalendar: values.clientCalendar,
            details: isNextRound
              ? [values.anotherRoundReason, values.details]
                  .filter(Boolean)
                  .join(' — ')
              : values.details,
            clientTimezone: values.clientTimezone,
          }),
        );

    if (values.resolution === ClientFeedbackResolution.Rejected) {
      logEvent('candidate_rejection_submitted');
    }
    if (values.resolution === ClientFeedbackResolution.CallRequested) {
      logEvent('interview_submitted');
    }
    if (values.resolution === ClientFeedbackResolution.HiringApproved) {
      logEvent('candidate_hired');
    }

    return (
      provideCandidateFeedback.fulfilled.match(res) ||
      provideClientInterviewFeedback.fulfilled.match(res)
    );
  };

  return (
    <ClientFeedbackDialog
      subject={
        data && {
          name: [data.profile?.firstName, data.profile?.lastName]
            .filter(Boolean)
            .join(' '),
          subtitle: candidateSubtitle(data),
          details: data,
          clientTimezone: latestCI?.clientTimezone,
        }
      }
      action={candidateDetailsAction}
      isLoading={isLoading}
      error={writeError}
      interviewRequested={candidateStatus === CandidateStatus.CallRequested}
      onClose={handleModalClose}
      onSubmit={handleSubmit}
    />
  );
};
